import { transparentize } from 'polished';
//
import colors from 'theme/colors';
import { IParetoProductWithDetails } from 'store/pareto/types';
import { EEvaluationUnit } from 'store/deal-evaluation/types';
import { formatNumber } from 'lib/helpers/currency';
import { EDisplay } from 'components/deal-results/product-items';
import { capitalizeFirstLetter } from 'lib/helpers/string-manipulators';
import { measureMaxTextWidth } from 'lib/helpers/visuals';
import labels from 'constants/labels';
import { select, event, symbol, symbolTriangle, Selection } from 'd3';
import {
  boldFontWeight,
  divideByFourValue,
  divideByTwoValue,
  measureMaxTextWidthValue,
  multiplyByTwoValue,
  tooltipHeightValue,
  tooltipPointerXValue,
  tooltipPointerYValue,
  tooltipRXAttrValue,
  tooltipRYAttrValue,
  tooltipTransparentizeValue,
  tooltipTriangleSizeValue,
} from 'constants/numericalValues';

export function mousemove() {
  const tooltip = select('.tooltip-pareto');
  tooltip.style('pointer-events', 'none');
  tooltip.attr('transform', `translate(${event.pageX - tooltipPointerXValue},${event.pageY - tooltipPointerYValue})`);
}

const tooltipWidth = 140;
const tooltipPadding = 10;
const fontWeight = 'font-weight';

export function drawTooltip(svg: Selection<SVGGElement, unknown, null, undefined>) {
  const tooltipTextVerticalSpacing = 15;
  const tooltipHeight = tooltipHeightValue * tooltipTextVerticalSpacing + multiplyByTwoValue * tooltipPadding;
  const tooltipOffset = 11;
  const tooltipTextXVal = -tooltipWidth / divideByTwoValue + tooltipPadding;
  const tooltipTextXValRightSide = tooltipWidth / divideByTwoValue - tooltipPadding;
  let tooltipTextCurrentYValue = -(
    tooltipHeight +
    tooltipOffset -
    tooltipPadding +
    tooltipTextVerticalSpacing / divideByFourValue
  );
  const tooltip = svg
    .append('g')
    .attr('class', 'tooltip-pareto')
    .style('font-size', '10px')
    .style('display', 'none');

  tooltip
    .append('path')
    .attr(
      'd',
      symbol()
        .type(symbolTriangle)
        .size(tooltipTriangleSizeValue)
    )
    .attr('fill', transparentize(tooltipTransparentizeValue, colors.darkBackgroundElevation1))
    .attr('transform', 'rotate(180) translate(0, 8)');

  tooltip
    .append('rect')
    .attr('class', 'tooltip-pareto-container')
    .attr('width', tooltipWidth)
    .attr('height', tooltipHeight)
    .attr('fill', transparentize(tooltipTransparentizeValue, colors.darkBackgroundElevation1))
    .style('text-align', 'center')
    .attr('x', -tooltipWidth / divideByTwoValue)
    .attr('y', -(tooltipHeight + tooltipOffset))
    .attr('rx', tooltipRXAttrValue)
    .attr('ry', tooltipRYAttrValue);

  const addText = (selection: Selection<SVGGElement, unknown, null, undefined>, className: string, yPos: number) => {
    return selection
      .append('text')
      .attr('font-size', '12px')
      .attr('class', `tooltip-pareto-${className}`)
      .attr('fill', 'white')
      .attr('x', tooltipTextXVal)
      .attr('y', yPos);
  };
  tooltipTextCurrentYValue += tooltipTextVerticalSpacing;
  addText(tooltip, 'customer', tooltipTextCurrentYValue);
  tooltipTextCurrentYValue += tooltipTextVerticalSpacing;
  addText(tooltip, 'deal-name', tooltipTextCurrentYValue);
  tooltipTextCurrentYValue += tooltipTextVerticalSpacing;
  addText(tooltip, 'region-product-line', tooltipTextCurrentYValue);
  tooltipTextCurrentYValue += tooltipTextVerticalSpacing;
  addText(tooltip, 'date-range', tooltipTextCurrentYValue);
  tooltipTextCurrentYValue += tooltipTextVerticalSpacing;
  addText(tooltip, 'product-name', tooltipTextCurrentYValue);
  tooltipTextCurrentYValue += multiplyByTwoValue * tooltipTextVerticalSpacing;
  addText(tooltip, 'selected-price-label', tooltipTextCurrentYValue).style('font-weight', boldFontWeight);
  tooltip
    .append('text')
    .attr('font-size', '12px')
    .attr('class', 'tooltip-pareto-selected-price-value')
    .style(fontWeight, boldFontWeight)
    .attr('x', tooltipTextXValRightSide)
    .attr('y', tooltipTextCurrentYValue)
    .attr('text-anchor', 'end')
    .attr('fill', 'white');
  tooltipTextCurrentYValue += tooltipTextVerticalSpacing;
  addText(tooltip, 'volume-label', tooltipTextCurrentYValue)
    .text('Volume')
    .style(fontWeight, boldFontWeight);

  tooltip
    .append('text')
    .attr('font-size', '12px')
    .attr('class', 'tooltip-pareto-volume-value')
    .style(fontWeight, boldFontWeight)
    .attr('x', tooltipTextXValRightSide)
    .attr('y', tooltipTextCurrentYValue)
    .attr('text-anchor', 'end')
    .attr('fill', 'white');

  return tooltip;
}

export function updateTooltipData(
  paretoProductWithDetails: IParetoProductWithDetails,
  selectedUnitType: EEvaluationUnit,
  selectedDisplay: EDisplay
) {
  const container = select('.tooltip-pareto-container');

  const customer = select('.tooltip-pareto-customer');
  const dealName = select('.tooltip-pareto-deal-name');
  const regionProductLine = select('.tooltip-pareto-region-product-line');
  const dateRange = select('.tooltip-pareto-date-range');
  const productName = select('.tooltip-pareto-product-name');
  const selectedPrice = select('.tooltip-pareto-selected-price-value');
  const selectedPriceLabel = select('.tooltip-pareto-selected-price-label');
  const volume = select('.tooltip-pareto-volume-value');
  const appendage = selectedUnitType === EEvaluationUnit.TON ? 'T' : 'LB';
  const currencySymbol = selectedUnitType === EEvaluationUnit.TON ? labels.currency.dollar : labels.currency.cents;
  const selectedPriceOutput = `${currencySymbol}${formatNumber(
    paretoProductWithDetails[selectedDisplay],
    0
  )}/${appendage}`;
  const volumeOutput = `${formatNumber(paretoProductWithDetails.totalVolume, 0)}${appendage}`;

  container.attr(
    'width',
    Math.max(
      tooltipWidth,
      measureMaxTextWidth([paretoProductWithDetails.customer, paretoProductWithDetails.dealName]) +
        multiplyByTwoValue * tooltipPadding,
      measureMaxTextWidthValue
    )
  );

  customer.text(paretoProductWithDetails.customer);
  dealName.text(paretoProductWithDetails.dealName);
  regionProductLine.text(paretoProductWithDetails.regionProductLine);
  dateRange.text(paretoProductWithDetails.dateRange);
  productName.text(paretoProductWithDetails.name);
  selectedPrice.text(selectedPriceOutput);
  selectedPriceLabel.text(capitalizeFirstLetter(selectedDisplay === EDisplay.SALES ? 'revenue' : selectedDisplay));
  volume.text(volumeOutput);
}
